import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './config';
import { Dog, DogFormData, DogSearchFilter, Gender } from '@/types/dog';
import { VaccinationType } from '@/types/vaccination';
import { normalizeRegistrationNumber } from '@/utils/nameNormalizer';

/**
 * 犬個体管理サービスクラス
 */
export class DogService {
  private readonly COLLECTION_NAME = 'dogs';

  /**
   * 新規個体登録
   */
  async createDog(data: DogFormData, userId: string): Promise<Dog> {
    try {
      // 生体番号の重複チェック
      if (data.bioNumber) {
        const isDuplicate = await this.checkBioNumberDuplicate(data.bioNumber, userId);
        if (isDuplicate) {
          throw new Error('この生体番号は既に登録されています');
        }
      } else {
        // 生体番号が未指定の場合は自動生成
        data.bioNumber = await this.generateBioNumber(userId);
      }

      // ドキュメントIDを生成
      const dogRef = doc(collection(db, this.COLLECTION_NAME));
      const dogId = dogRef.id;

      const now = Timestamp.now();

      const dog: Dog = {
        id: dogId,
        ownerId: userId,
        bioNumber: data.bioNumber,
        name: data.name,
        gender: data.gender,
        birthDate: Timestamp.fromDate(data.birthDate),
        fatherId: data.fatherId,
        motherId: data.motherId,
        status: data.status,
        notes: data.notes,
        characteristics: data.characteristics,
        createdAt: now,
        updatedAt: now,
        updatedBy: userId,
        // PDF解析から抽出される追加フィールド
        breed: data.breed,
        officialName: data.officialName,
        microchipNumber: data.microchipNumber,
        microchipPin: data.microchipPin,
        jkcRegistrationNumber: data.jkcRegistrationNumber,
        dnaRegistrationNumber: data.dnaRegistrationNumber,
        breederName: data.breederName,
        ownerName: data.ownerName,
        geneticTestResults: data.geneticTestResults,
      };

      // undefinedの値を削除（Firestoreはundefinedを許可しない）
      const cleanedDog = Object.fromEntries(
        Object.entries(dog).filter(([, value]) => value !== undefined)
      ) as Dog;

      await setDoc(dogRef, cleanedDog);

      return cleanedDog;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体登録に失敗しました: ${error.message}`);
      }
      throw new Error('個体登録に失敗しました');
    }
  }

  /**
   * 個体情報取得（単体）
   */
  async getDog(dogId: string, userId: string): Promise<Dog | null> {
    try {
      const dogRef = doc(db, this.COLLECTION_NAME, dogId);
      const dogSnap = await getDoc(dogRef);

      if (!dogSnap.exists()) {
        return null;
      }

      const dog = dogSnap.data() as Dog;

      // 自身の個体のみアクセス可能
      if (dog.ownerId !== userId) {
        throw new Error('この個体にアクセスする権限がありません');
      }

      return dog;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体情報の取得に失敗しました: ${error.message}`);
      }
      throw new Error('個体情報の取得に失敗しました');
    }
  }

  /**
   * 個体一覧取得（フィルタ・ソート対応）
   */
  async getDogs(
    userId: string,
    filter?: {
      gender?: Gender;
      status?: string;
    },
    sortBy: 'birthDate' | 'name' | 'createdAt' = 'birthDate',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<Dog[]> {
    try {
      const constraints: QueryConstraint[] = [where('ownerId', '==', userId)];

      // フィルタ条件追加
      if (filter?.gender) {
        constraints.push(where('gender', '==', filter.gender));
      }
      if (filter?.status) {
        constraints.push(where('status', '==', filter.status));
      }

      // ソート条件追加
      constraints.push(orderBy(sortBy, sortOrder));

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const querySnapshot = await getDocs(q);

      const dogs: Dog[] = [];
      querySnapshot.forEach((doc) => {
        dogs.push(doc.data() as Dog);
      });

      return dogs;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体一覧の取得に失敗しました: ${error.message}`);
      }
      throw new Error('個体一覧の取得に失敗しました');
    }
  }

  /**
   * 個体情報更新
   */
  async updateDog(dogId: string, data: Partial<DogFormData>, userId: string): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingDog = await this.getDog(dogId, userId);
      if (!existingDog) {
        throw new Error('個体が見つかりません');
      }

      // 生体番号変更時の重複チェック
      if (data.bioNumber && data.bioNumber !== existingDog.bioNumber) {
        const isDuplicate = await this.checkBioNumberDuplicate(data.bioNumber, userId);
        if (isDuplicate) {
          throw new Error('この生体番号は既に登録されています');
        }
      }

      const dogRef = doc(db, this.COLLECTION_NAME, dogId);

      const updateData: Record<string, unknown> = {
        ...data,
        birthDate: data.birthDate ? Timestamp.fromDate(data.birthDate) : undefined,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
      };

      // undefinedの値を削除
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(dogRef, updateData);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体情報の更新に失敗しました: ${error.message}`);
      }
      throw new Error('個体情報の更新に失敗しました');
    }
  }

  /**
   * 個体削除
   */
  async deleteDog(dogId: string, userId: string, permanent = false): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingDog = await this.getDog(dogId, userId);
      if (!existingDog) {
        throw new Error('個体が見つかりません');
      }

      const dogRef = doc(db, this.COLLECTION_NAME, dogId);

      if (permanent) {
        // 物理削除
        await deleteDoc(dogRef);
      } else {
        // 論理削除（ステータスを deceased に変更）
        await updateDoc(dogRef, {
          status: 'deceased',
          updatedAt: Timestamp.now(),
          updatedBy: userId,
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体の削除に失敗しました: ${error.message}`);
      }
      throw new Error('個体の削除に失敗しました');
    }
  }

  /**
   * 個体検索（高度なフィルタリング）
   */
  async searchDogs(userId: string, filter: DogSearchFilter): Promise<Dog[]> {
    try {
      // まず全個体を取得
      let dogs = await this.getDogs(userId);

      // クライアント側でフィルタリング（Firestoreの複合クエリ制限のため）
      if (filter.name) {
        const searchName = filter.name.toLowerCase();
        dogs = dogs.filter((dog) => dog.name.toLowerCase().includes(searchName));
      }

      if (filter.bioNumber) {
        const searchBioNumber = filter.bioNumber.toLowerCase();
        dogs = dogs.filter((dog) => dog.bioNumber.toLowerCase().includes(searchBioNumber));
      }

      // 登録番号全般での検索（JKC・DNA・マイクロチップ番号・暗証記号の部分一致）
      if (filter.registrationNumber) {
        const normalizedQuery = normalizeRegistrationNumber(filter.registrationNumber);
        if (normalizedQuery) {
          dogs = dogs.filter((dog) => {
            const candidates = [
              dog.jkcRegistrationNumber,
              dog.dnaRegistrationNumber,
              dog.microchipNumber,
              dog.microchipPin,
            ];
            return candidates.some(
              (value) =>
                value && normalizeRegistrationNumber(value).includes(normalizedQuery)
            );
          });
        }
      }

      if (filter.gender) {
        dogs = dogs.filter((dog) => dog.gender === filter.gender);
      }

      if (filter.status) {
        dogs = dogs.filter((dog) => dog.status === filter.status);
      }

      if (filter.birthDateFrom) {
        const fromTimestamp = Timestamp.fromDate(filter.birthDateFrom);
        dogs = dogs.filter((dog) => dog.birthDate.toMillis() >= fromTimestamp.toMillis());
      }

      if (filter.birthDateTo) {
        const toTimestamp = Timestamp.fromDate(filter.birthDateTo);
        dogs = dogs.filter((dog) => dog.birthDate.toMillis() <= toTimestamp.toMillis());
      }

      // 交配時期フィルタ（交配・出産記録ベース）
      if (
        filter.hasMatingRecord ||
        filter.hasBirthRecord ||
        filter.isBreedingAvailable ||
        filter.matingPeriodFrom ||
        filter.matingPeriodTo
      ) {
        dogs = await this.filterByBreedingActivity(dogs, userId, filter);
      }

      // ワクチン未接種フィルタ（指定期間内に指定種類の接種記録がない個体）
      if (filter.unvaccinatedTypes && filter.unvaccinatedTypes.length > 0) {
        dogs = await this.filterByUnvaccinated(dogs, filter);
      }

      return dogs;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`個体検索に失敗しました: ${error.message}`);
      }
      throw new Error('個体検索に失敗しました');
    }
  }

  /**
   * ワクチン未接種フィルタ（非公開メソッド）
   *
   * 各個体のワクチン接種記録サブコレクションを横断検索し、
   * 指定期間内に指定種類の接種記録が 1 件もない個体を抽出する。
   * Firestore の制約によりクライアント側で集約する。
   */
  private async filterByUnvaccinated(
    dogs: Dog[],
    filter: DogSearchFilter
  ): Promise<Dog[]> {
    const types = filter.unvaccinatedTypes ?? [];
    const fromMs = filter.unvaccinatedPeriodFrom
      ? Timestamp.fromDate(filter.unvaccinatedPeriodFrom).toMillis()
      : null;
    const toMs = filter.unvaccinatedPeriodTo
      ? Timestamp.fromDate(filter.unvaccinatedPeriodTo).toMillis()
      : null;

    const result: Dog[] = [];

    for (const dog of dogs) {
      const snapshot = await getDocs(
        collection(db, this.COLLECTION_NAME, dog.id, 'vaccinations')
      );

      const hasMatchingRecord = snapshot.docs.some((d) => {
        const data = d.data() as {
          type: VaccinationType;
          vaccinatedAt: Timestamp;
        };
        if (!types.includes(data.type)) {
          return false;
        }
        const vaccinatedMs = data.vaccinatedAt.toMillis();
        if (fromMs !== null && vaccinatedMs < fromMs) {
          return false;
        }
        if (toMs !== null && vaccinatedMs > toMs) {
          return false;
        }
        return true;
      });

      // 接種記録が「1件もない」個体を抽出
      if (!hasMatchingRecord) {
        result.push(dog);
      }
    }

    return result;
  }

  /**
   * 交配活動に基づくフィルタリング（非公開メソッド）
   */
  private async filterByBreedingActivity(
    dogs: Dog[],
    userId: string,
    filter: DogSearchFilter
  ): Promise<Dog[]> {
    // 交配・出産記録の取得が必要な場合は、ここで取得
    // 注: このメソッドは循環参照を避けるため、直接Firestoreを呼び出す
    const filteredDogs: Dog[] = [];

    for (const dog of dogs) {
      let shouldInclude = true;

      // 交配記録チェック
      if (filter.hasMatingRecord) {
        const hasMating = await this.checkHasMatingRecord(dog.id, userId);
        if (!hasMating) {
          shouldInclude = false;
        }
      }

      // 出産記録チェック
      if (shouldInclude && filter.hasBirthRecord) {
        const hasBirth = await this.checkHasBirthRecord(dog.id, userId);
        if (!hasBirth) {
          shouldInclude = false;
        }
      }

      // 交配可能チェック（メス犬のみ）
      if (shouldInclude && filter.isBreedingAvailable) {
        if (dog.gender !== 'female') {
          shouldInclude = false;
        } else {
          const isAvailable = await this.checkIsBreedingAvailable(dog.id, userId);
          if (!isAvailable) {
            shouldInclude = false;
          }
        }
      }

      // 交配期間チェック
      if (shouldInclude && (filter.matingPeriodFrom || filter.matingPeriodTo)) {
        const hasRecordInPeriod = await this.checkHasRecordInPeriod(
          dog.id,
          userId,
          filter.matingPeriodFrom,
          filter.matingPeriodTo
        );
        if (!hasRecordInPeriod) {
          shouldInclude = false;
        }
      }

      if (shouldInclude) {
        filteredDogs.push(dog);
      }
    }

    return filteredDogs;
  }

  /**
   * 交配記録の有無をチェック
   */
  private async checkHasMatingRecord(dogId: string, userId: string): Promise<boolean> {
    const matingQuery = query(
      collection(db, 'matingRecords'),
      where('ownerId', '==', userId)
    );
    const matingSnapshot = await getDocs(matingQuery);

    return matingSnapshot.docs.some((doc) => {
      const data = doc.data();
      return data.maleId === dogId || data.femaleId === dogId;
    });
  }

  /**
   * 出産記録の有無をチェック
   */
  private async checkHasBirthRecord(dogId: string, userId: string): Promise<boolean> {
    const birthQuery = query(
      collection(db, 'birthRecords'),
      where('ownerId', '==', userId)
    );
    const birthSnapshot = await getDocs(birthQuery);

    return birthSnapshot.docs.some((doc) => {
      const data = doc.data();
      return data.motherId === dogId || data.fatherId === dogId;
    });
  }

  /**
   * 交配可能かチェック（メス犬のみ）
   */
  private async checkIsBreedingAvailable(dogId: string, userId: string): Promise<boolean> {
    // 出産記録を取得
    const birthQuery = query(
      collection(db, 'birthRecords'),
      where('ownerId', '==', userId),
      where('motherId', '==', dogId),
      orderBy('birthDate', 'desc')
    );
    const birthSnapshot = await getDocs(birthQuery);

    // 出産記録がない場合は交配可能
    if (birthSnapshot.empty) {
      return true;
    }

    // 最新の出産日を取得
    const latestBirth = birthSnapshot.docs[0].data();
    const lastBirthDate = (latestBirth.birthDate as Timestamp).toDate();

    // 6ヶ月（180日）経過しているかチェック
    const REST_PERIOD_DAYS = 180;
    const nextAvailableDate = new Date(lastBirthDate);
    nextAvailableDate.setDate(nextAvailableDate.getDate() + REST_PERIOD_DAYS);

    const today = new Date();
    return today >= nextAvailableDate;
  }

  /**
   * 指定期間内に交配・出産記録があるかチェック
   */
  private async checkHasRecordInPeriod(
    dogId: string,
    userId: string,
    periodFrom?: Date,
    periodTo?: Date
  ): Promise<boolean> {
    if (!periodFrom && !periodTo) {
      return true;
    }

    const fromTime = periodFrom ? Timestamp.fromDate(periodFrom) : null;
    const toTime = periodTo ? Timestamp.fromDate(periodTo) : null;

    // 交配記録をチェック
    const matingQuery = query(
      collection(db, 'matingRecords'),
      where('ownerId', '==', userId)
    );
    const matingSnapshot = await getDocs(matingQuery);

    const hasMatingInPeriod = matingSnapshot.docs.some((doc) => {
      const data = doc.data();
      if (data.maleId !== dogId && data.femaleId !== dogId) {
        return false;
      }

      const matingDate = data.matingDate as Timestamp;
      const matingTime = matingDate.toMillis();

      if (fromTime && matingTime < fromTime.toMillis()) {
        return false;
      }
      if (toTime && matingTime > toTime.toMillis()) {
        return false;
      }

      return true;
    });

    if (hasMatingInPeriod) {
      return true;
    }

    // 出産記録をチェック
    const birthQuery = query(
      collection(db, 'birthRecords'),
      where('ownerId', '==', userId)
    );
    const birthSnapshot = await getDocs(birthQuery);

    const hasBirthInPeriod = birthSnapshot.docs.some((doc) => {
      const data = doc.data();
      if (data.motherId !== dogId && data.fatherId !== dogId) {
        return false;
      }

      const birthDate = data.birthDate as Timestamp;
      const birthTime = birthDate.toMillis();

      if (fromTime && birthTime < fromTime.toMillis()) {
        return false;
      }
      if (toTime && birthTime > toTime.toMillis()) {
        return false;
      }

      return true;
    });

    return hasBirthInPeriod;
  }

  /**
   * 血統図取得（3世代）
   */
  async getPedigree(dogId: string, userId: string): Promise<{
    dog: Dog;
    father?: Dog;
    mother?: Dog;
    paternalGrandfather?: Dog;
    paternalGrandmother?: Dog;
    maternalGrandfather?: Dog;
    maternalGrandmother?: Dog;
  }> {
    try {
      const dog = await this.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const result: Record<string, Dog | undefined> = { dog };

      // 父犬取得
      if (dog.fatherId) {
        const father = await this.getDog(dog.fatherId, userId);
        if (father) {
          result.father = father;

          // 父方の祖父母
          if (father.fatherId) {
            const paternalGrandfather = await this.getDog(father.fatherId, userId);
            if (paternalGrandfather) {
              result.paternalGrandfather = paternalGrandfather;
            }
          }
          if (father.motherId) {
            const paternalGrandmother = await this.getDog(father.motherId, userId);
            if (paternalGrandmother) {
              result.paternalGrandmother = paternalGrandmother;
            }
          }
        }
      }

      // 母犬取得
      if (dog.motherId) {
        const mother = await this.getDog(dog.motherId, userId);
        if (mother) {
          result.mother = mother;

          // 母方の祖父母
          if (mother.fatherId) {
            const maternalGrandfather = await this.getDog(mother.fatherId, userId);
            if (maternalGrandfather) {
              result.maternalGrandfather = maternalGrandfather;
            }
          }
          if (mother.motherId) {
            const maternalGrandmother = await this.getDog(mother.motherId, userId);
            if (maternalGrandmother) {
              result.maternalGrandmother = maternalGrandmother;
            }
          }
        }
      }

      return result as {
        dog: Dog;
        father?: Dog;
        mother?: Dog;
        paternalGrandfather?: Dog;
        paternalGrandmother?: Dog;
        maternalGrandfather?: Dog;
        maternalGrandmother?: Dog;
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統図の取得に失敗しました: ${error.message}`);
      }
      throw new Error('血統図の取得に失敗しました');
    }
  }

  /**
   * 生体番号の重複チェック
   */
  private async checkBioNumberDuplicate(bioNumber: string, userId: string): Promise<boolean> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('ownerId', '==', userId),
      where('bioNumber', '==', bioNumber)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  }

  /**
   * 生体番号の自動生成
   */
  private async generateBioNumber(userId: string): Promise<string> {
    const year = new Date().getFullYear();
    const dogs = await this.getDogs(userId);

    // 今年登録された個体の数を取得
    const thisYearDogs = dogs.filter((dog) => {
      const dogYear = dog.createdAt.toDate().getFullYear();
      return dogYear === year;
    });

    const sequence = (thisYearDogs.length + 1).toString().padStart(3, '0');
    return `DOG-${year}-${sequence}`;
  }
}

// シングルトンインスタンスをエクスポート
export const dogService = new DogService();
